import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  CONVERSATIONS_PAGE_SIZE,
  ConversationListItemResponse,
  ConversationSupportAudience,
  ConversationType,
  getConversations,
  queryKeys,
  useChatSocketState,
  useDebouncedValue,
} from '@vgrubs/components';
import { useCallback, useEffect, useRef } from 'react';

const PRELOAD_ROOT_MARGIN = '0px 0px 600px 0px';
const SEARCH_DEBOUNCE_MS = 500;
/**
 * How far down the list still counts as being at the top - where a refetch
 * cannot pull a row out from under the pointer. See `useAutoRefresh`.
 */
const AUTO_REFRESH_TOP_PX = 24;

export type ChatInboxFilters = {
  query: string;
  /**
   * What this tab is, in the endpoint's own terms - see `ChatScope`. The two
   * support tabs send an audience, the internal one sends types, and neither
   * ever sends both: `supportAudience` already implies `support`.
   */
  types?: ConversationType[];
  supportAudience?: ConversationSupportAudience;
  /** The shelf rather than the inbox - see `ChatScope`. */
  archived: boolean;
  unansweredOnly: boolean;
  unreadOnly: boolean;
};

/**
 * Keyset-paginated inbox.
 *
 * Unlike every other list in this app, the cursor is the *last row of the
 * previous page* rather than a page number. That's also why the socket can't
 * freely splice rows around: reordering the list invalidates the cursors that
 * have already been issued. See `useConversationsSocket` - it only mutates page
 * 0 and otherwise sets `inboxStale`, which this hook clears by refetching (see
 * the auto-refresh below) rather than by drifting.
 */
export const useChatInbox = (
  filters: ChatInboxFilters,
  /**
   * The element the list scrolls inside, when there is one. Without it the
   * observer roots on the viewport, and the intersection is then clipped by
   * that element anyway - so the `rootMargin` preload below never applies and
   * the next page only loads once you are already at the very bottom.
   */
  scrollRootRef?: React.RefObject<HTMLElement | null>
) => {
  const queryClient = useQueryClient();
  const debouncedQuery = useDebouncedValue(filters.query, SEARCH_DEBOUNCE_MS);
  const observerRef = useRef<HTMLDivElement | null>(null);

  const inboxStale = useChatSocketState((state) => state.inboxStale);
  const setInboxStale = useChatSocketState((state) => state.setInboxStale);
  const setActiveInboxKey = useChatSocketState(
    (state) => state.setActiveInboxKey
  );
  const setActiveInboxScope = useChatSocketState(
    (state) => state.setActiveInboxScope
  );

  /** What this tab is, as one string: the audience, or the joined types. */
  const scope = filters.supportAudience ?? filters.types?.join(',') ?? null;

  const queryKey = queryKeys.conversations.inbox(
    scope,
    debouncedQuery || null,
    filters.archived,
    filters.unansweredOnly,
    filters.unreadOnly
  );

  /*
   * Tell the socket which list is on screen. Without it, it would write into
   * the unfiltered key while the user is looking at a filtered one, and it
   * would arm the refresh pill for traffic that arrived while the chat page
   * was closed entirely. Cleared on unmount for exactly that reason.
   */
  useEffect(() => {
    setActiveInboxKey(queryKey);
    setActiveInboxScope(scope);
    return () => {
      setActiveInboxKey(null);
      setActiveInboxScope(null);
    };
    // `queryKey` is a fresh array each render; its contents are the identity.
  }, [setActiveInboxKey, setActiveInboxScope, scope, ...queryKey]);

  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const { data } = await getConversations({
        ...(filters.types ? { types: filters.types } : {}),
        ...(filters.supportAudience
          ? { supportAudience: filters.supportAudience }
          : {}),
        ...(debouncedQuery ? { query: debouncedQuery } : {}),
        archived: filters.archived,
        unansweredOnly: filters.unansweredOnly,
        unreadOnly: filters.unreadOnly,
        limit: CONVERSATIONS_PAGE_SIZE,
        ...(pageParam ?? {}),
      });
      return data.data;
    },
    initialPageParam: undefined as
      | { cursorSortAt: string | null; cursorId: number }
      | undefined,
    getNextPageParam: (lastPage: ConversationListItemResponse[]) => {
      if (lastPage.length < CONVERSATIONS_PAGE_SIZE) return undefined;
      const last = lastPage[lastPage.length - 1];
      return {
        // Nullable for a conversation with no messages - pass it through as-is
        // and let the backend decide where those sort.
        cursorSortAt: last.lastMessageAt,
        cursorId: last.id,
      };
    },
    // Live updates come over the socket; a focus refetch here would re-fetch
    // every loaded page in sequence.
    refetchOnWindowFocus: false,
  });

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query;

  useEffect(() => {
    const sentinel = observerRef.current;
    if (!sentinel) return undefined;

    let observer: IntersectionObserver | null = null;

    const attach = () => {
      observer?.disconnect();

      /*
       * Root on the column only while it actually scrolls. A root element that
       * never scrolls always contains the sentinel, which would pull every page
       * in at once on mount - so the layout, not the breakpoint, decides.
       */
      const element = scrollRootRef?.current ?? null;
      const overflowY = element ? getComputedStyle(element).overflowY : '';
      const root =
        overflowY === 'auto' || overflowY === 'scroll' ? element : null;

      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        { root, rootMargin: PRELOAD_ROOT_MARGIN }
      );
      observer.observe(sentinel);
    };

    attach();

    // Re-root when the layout crosses the two-pane breakpoint. Listening to the
    // media query rather than `resize` keeps this to one call per crossing.
    const media = window.matchMedia('(min-width: 1201px)');
    media.addEventListener('change', attach);

    return () => {
      media.removeEventListener('change', attach);
      observer?.disconnect();
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, scrollRootRef]);

  /**
   * `resetQueries`, not `invalidateQueries`: invalidating an infinite query
   * refetches every page it has loaded, one after another, and the cursors
   * those pages were fetched with may no longer line up.
   */
  const refresh = useCallback(() => {
    queryClient.resetQueries({ queryKey: queryKeys.conversations.inboxAll });
    setInboxStale(false);
  }, [queryClient, setInboxStale]);

  /**
   * The rows to render - the query's own, or the last set it had.
   *
   * `refresh` resets the query rather than invalidating it (see above), which
   * throws the data away and puts React Query back in `pending`. Rendered
   * literally, that blanks the inbox and flashes a full-panel spinner over it
   * every time somebody is added to a group or a conversation is created -
   * work the user just did, in a list they are looking at.
   *
   * So the previous rows stay on screen until the new ones arrive, and the
   * spinner is left for the one case it means anything: nothing has ever
   * loaded. Dropped when the FILTERS change, because then the old rows are
   * genuinely the wrong answer and a spinner is the honest thing to show.
   */
  const lastThreadsRef = useRef<ConversationListItemResponse[]>([]);
  const lastKeyRef = useRef(JSON.stringify(queryKey));

  const currentKey = JSON.stringify(queryKey);
  if (lastKeyRef.current !== currentKey) {
    lastKeyRef.current = currentKey;
    lastThreadsRef.current = [];
  }

  const fetched = query.data?.pages.flat();
  if (fetched) lastThreadsRef.current = fetched;

  const threads: ConversationListItemResponse[] =
    fetched ?? lastThreadsRef.current;

  /**
   * Bring the list up to date by itself, rather than parking a button on top of
   * it.
   *
   * The refresh used to be offered, because a reset threw the rows away and
   * blanked the panel. It no longer does - the previous rows stay on screen
   * until the new ones land (see above) - so there is nothing left for the user
   * to decide: a pill that says "conversations updated" is asking permission to
   * do the only thing anyone would ever pick.
   *
   * The one thing a refetch can still disturb is somebody reading DEEP in the
   * list: the reset drops every page after the first, so a list scrolled past
   * row 30 would collapse under them. So it waits in that case, and applies the
   * moment they scroll back to the top. Nothing is lost by waiting - the flag
   * stays set, and the rows they are reading are the ones they asked for.
   */
  const pagesLoaded = query.data?.pages.length ?? 0;

  useEffect(() => {
    if (!inboxStale || isFetchingNextPage) return undefined;

    const element = scrollRootRef?.current ?? null;

    const applyWhenSafe = () => {
      // One page is the whole list, so resetting it cannot change its length -
      // safe wherever the reader happens to be inside it.
      const readingDeeper =
        pagesLoaded > 1 && !!element && element.scrollTop > AUTO_REFRESH_TOP_PX;
      if (!readingDeeper) refresh();
    };

    applyWhenSafe();

    if (!element) return undefined;
    element.addEventListener('scroll', applyWhenSafe, { passive: true });
    return () => element.removeEventListener('scroll', applyWhenSafe);
  }, [inboxStale, isFetchingNextPage, pagesLoaded, refresh, scrollRootRef]);

  return {
    threads,
    observerRef,
    isFetchingNextPage,
    isLoading: query.isLoading && threads.length === 0,
    status: query.status,
    /** For the page's own writes - a new conversation, an archive, a rename. */
    refresh,
  };
};

/** What the page hands down to the inbox pane. */
export type UseChatInbox = ReturnType<typeof useChatInbox>;
